import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import * as sdk from '@tonupordown/sdk/lib/sdk/index.js';
import { io } from 'socket.io-client';

const apiUrl = import.meta.env.VITE_API_URL;

async function handleUser(
  connection: sdk.IConnection, 
  setData: React.Dispatch<React.SetStateAction<SiteData>>,
  data: SiteData
) {
  try {
    const user = await sdk.functional.users.findOne(connection);

    if (user && 'id' in user) {
      const userNeedsUpdate = 
        user.first_name !== data.user.first_name || 
        user.last_name !== data.user.last_name || 
        user.username !== data.user.username || 
        user.is_premium !== data.user.is_premium;

      if (userNeedsUpdate) {
        try {
          await sdk.functional.users.update(connection);
        } catch (updateError) {
          console.log('Failed to update user', updateError);
        }
      }

      updateUserState(user, setData);
    }
  } catch (error) {
    if (error instanceof sdk.HttpError && error.status === 404) {
      console.log('User not found', error);
      try {
        const response = await sdk.functional.users.create(connection);
        if ('balance' in response) {
          updateUserState(response, setData);
          console.log('User created', response);
        }
      } catch (createError) {
        console.log('Failed to create user', createError);
      }
    } else {
      console.log('Error fetching user', error);
    }
  }
}

function updateUserState(user: any, setData: React.Dispatch<React.SetStateAction<SiteData>>) {
  setData((prevData: SiteData) => ({ 
    ...prevData, 
    user: {
      ...prevData.user, 
      balance: user.balance,
      point: user.point,
      inviter_id: user.inviter_id,
      joined_at: user.joined_at,
      status: user.status,
      task: user.task ? JSON.parse(user.task) : null
    } 
  }));
}

// Define the shape of your site data
interface SiteData {
  user: {
    id : number
    first_name : string
    is_bot : boolean | null
    last_name : string | null
    username : string | null
    language_code : string | null
    is_premium : boolean | null
    status : string | null
    balance : number | null
    inviter_id : number | null
    joined_at : string | null
    point : number | null
    task : any | null
    photo_url : string | null
  }
}

interface SiteDataContextValue {
  data: SiteData;
  webApp: any;
  updateData: (part: string, time: number) => void;
  events: any[];
  claimReward: (task_type: string, task_id: string) => boolean;
}

export function validateData(initData: string): Record<string, any> {
  const params = new URLSearchParams(initData);
  const data: Record<string, any> = {};

  for (const [key, value] of params) {
    if (key !== 'hash') {
      data[key] = key === 'user' ? JSON.parse(value) : value;
    }
  }

  return data;
}

// Create a context with a default value
const SiteDataContext = createContext<SiteDataContextValue | undefined>(undefined);

// Function to load data from localStorage
function loadDataFromLocalStorage(): SiteData | null {
  const storedData = localStorage.getItem('siteData');
  return storedData ? JSON.parse(storedData) : null;
}

// Function to save data to localStorage
function saveDataToLocalStorage(data: SiteData) {
  localStorage.setItem('siteData', JSON.stringify(data));
}

// Create a provider component
export const SiteDataProvider = ({ children }: { children: React.ReactNode }) => {
  const [webApp, setWebApp] = useState<any | null>(null);
  const [initData, setInitData] = useState<string | null>(null);
  const [connection, setConnection] = useState<sdk.IConnection | null>(null);

  // Load initial data from localStorage
  const initialData = loadDataFromLocalStorage() || {
    user: {
      id: 0,
      is_bot: false,
      first_name: '',
      last_name: '',
      username: '',
      language_code: '',
      is_premium: false,
      status: '',
      balance: 0,
      inviter_id: 0,
      joined_at: '',
      point: 0,
      task: null,
      photo_url: null,
    }
  };

  const [data, setData] = useState<SiteData>(initialData);
  const [events, setEvents] = useState<any[]>([]);

  useEffect(() => {
    if (window.Telegram && window.Telegram.WebApp) {
      try {
        window.Telegram.WebApp.ready();
        setWebApp(window.Telegram.WebApp);
      } catch (error) {
        console.error('Error initializing Telegram Web App:', error);
      }
    } else {
      console.warn('Telegram WebApp is not available');
    }
  }, []);

  useEffect(() => {
    if (webApp) {
      setInitData(webApp.initData);
    }
  }, [webApp]);

  useEffect(() => {
    if (initData) {
      setConnection({
        host: apiUrl,
        headers: {
          'init-data': initData,
        },
      });

      const parsedData = validateData(initData);
      setData((prevData) => ({ ...prevData, user: { ...prevData.user, ...parsedData.user } }));
    }
  }, [initData]);

  useEffect(() => {
    if (connection) {
      handleUser(connection, setData, data);

      // Set up an interval to send update requests every 5 seconds
      const intervalId = setInterval(() => {
        handleUser(connection, setData, data);
      }, 5000);

      // Clean up the interval on component unmount
      return () => clearInterval(intervalId);
    }
  }, [connection]);

  useEffect(() => {
    if (data.user.id) {
      console.log(data.user);
      // Save data to localStorage whenever it changes
      saveDataToLocalStorage(data);
    }
  }, [data]);

  useEffect(() => {
    if (apiUrl) {
      const socket = io(apiUrl);
      let timestampCounter = Math.floor(Date.now() / 1000); // Current Unix timestamp

      socket.on('connect', () => {
        console.log('Connected to socket server');
      });

      socket.on('priceUpdate', (priceData) => {
        setEvents((prevEvents) => {
          const updatedEvents = [
            ...prevEvents,
            {
              open_timestamp: timestampCounter,
              index_price: priceData.index_price,
            },
          ];

          // Ensure the events array does not exceed 200 records
          if (updatedEvents.length > 30) {
            updatedEvents.splice(0, updatedEvents.length - 30);
          }
          timestampCounter += 1;
          return updatedEvents;
        });
      });

      socket.on('disconnect', () => {
        console.log('Disconnected from socket server');
      });

      // Clean up the socket connection on component unmount
      return () => {
        console.log('Disconnecting socket');
        socket.disconnect();
      };
    }
  }, [apiUrl]);

  // Function to update the data after a delay
  const updateData = (parameter: string, delay: number = 0) => {
    if (!connection) return;

    if (parameter === 'user') {
      setTimeout(() => {
        handleUser(connection, setData, data);
      }, delay);
    }
  };

  const claimReward = (task_type: string, task_id: string) => {
    if (!connection) return false;

    sdk.functional.users.task.addTask(connection, {
      taskType: task_type,
      taskId: task_id,
    });
    return true;
  };

  // Memoize the context value
  const contextValue = useMemo(() => ({ data, webApp, updateData, events, claimReward }), [data, webApp, events]);

  return (
    <SiteDataContext.Provider value={contextValue}>
      {children}
    </SiteDataContext.Provider>
  );
};

// Custom hook to use the site data
export const useSiteData = () => {
  const context = useContext(SiteDataContext);
  if (context === undefined) {
    throw new Error('useSiteData must be used within a SiteDataProvider');
  }
  return context;
}; 